import { useState, useEffect } from "react";

import ElementInspectorItem, { ElementInfo } from "./ElementInspectorItem";

interface ElementInspectorProps {
  isActive: boolean;
  allActive: boolean;
  setIsActive: (isActive: boolean) => void;
  setAllActive: (allActive: boolean) => void;
  setAllElements: (elements: { element: HTMLElement; info: ElementInfo }[]) => void;
  onSelectElement: (element: HTMLElement) => void;
}

function ElementInspector({ isActive, allActive, setIsActive, setAllActive, onSelectElement, setAllElements }: ElementInspectorProps): JSX.Element {
  const [elements, setElements] = useState<{ element: HTMLElement; info: ElementInfo }[]>([]);

  /**
   * enable keyboard shortcuts to the inspector
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.shiftKey && event.key === "I") {
        setIsActive(true);
      }

      if (event.shiftKey && event.key === "A") {
        setAllActive(true);
      }

      // Escape key to deactivate
      if (event.key === "Escape") {
        setIsActive(false);
        setAllActive(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * turn on the inspector
   */
  useEffect(() => {
    if (!isActive) {
      return;
    }

    const toolkitInspector = document.getElementById("toolkitInspector");
    if (!toolkitInspector) {
      return;
    }

    //remove all the links and onclick from the toolkit inspector
    toolkitInspector.querySelectorAll("*").forEach((el) => {
      if (el instanceof HTMLAnchorElement) {
        el.setAttribute("original-href", el.href);
        el.removeAttribute("href");
        el.style.pointerEvents = "none";
      }
      if (el instanceof HTMLElement && typeof el.onclick === "function") {
        el.onclick = null;
      }
      el.removeEventListener?.("click", () => {});
    });

    //handle mouse or click events to show inspector on element
    const handleMouseOrClick = (event: MouseEvent): void => {
      event.preventDefault();
      event.stopPropagation();

      if (!event?.target || !(event.target instanceof HTMLElement)) {
        return;
      }

      const mouseX = event.clientX;
      const mouseY = event.clientY;

      //find all elements with the component-id attribute
      const editableComponents = (event.target as HTMLElement).querySelectorAll<HTMLElement>("[component-id]");
      let editableComponent: HTMLElement | null = null;
      for (const editableOption of Array.from(editableComponents ?? [])) {
        const componentId = editableOption.getAttribute("component-id");
        if (componentId) {
          editableComponent = editableOption;

          //calculate the position of the element to make sure the pointer is over the element
          const componentX = editableOption.getBoundingClientRect().x;
          const componentY = editableOption.getBoundingClientRect().y;
          const componentW = editableOption.getBoundingClientRect().width;
          const componentH = editableOption.getBoundingClientRect().height;

          //make sure the mouse is over the element
          if (mouseX >= componentX && mouseX <= componentX + componentW && mouseY >= componentY && mouseY <= componentY + componentH) {
            editableComponent = editableOption;
            break;
          }
        }
      }

      if (!editableComponent) {
        return;
      }

      setElements([
        {
          element: editableComponent as HTMLElement,
          info: {
            tagName: editableComponent.tagName.toLowerCase(),
            id: editableComponent.id,
            classList: Array.from(editableComponent.classList),
            componentId: editableComponent.getAttribute("component-id") || "",
          },
        },
      ]);
      onSelectElement(editableComponent as HTMLElement);
    };

    //attach listeners to the toolkit inspector
    //document.addEventListener("mouseover", handleMouseOrClick, { passive: false });
    toolkitInspector.addEventListener("click", handleMouseOrClick, { passive: false });
    return () => {
      //document.removeEventListener("mouseover", handleMouseOrClick);
      toolkitInspector.removeEventListener("click", handleMouseOrClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  /**
   * show all elements for the section
   */
  useEffect(() => {
    if (!allActive) {
      setElements([]);
      return;
    }

    const toolkitInspector = document.getElementById("toolkitInspector");
    if (!toolkitInspector) {
      return;
    }

    const elementsWithComponentId: { element: HTMLElement; info: ElementInfo }[] = [];
    const elements = toolkitInspector.querySelectorAll<HTMLElement>("[component-id]");

    elements.forEach((element) => {
      const componentId = element.getAttribute("component-id");
      if (componentId) {
        elementsWithComponentId.push({
          element: element as HTMLElement,
          info: {
            tagName: element.tagName.toLowerCase(),
            id: element.id,
            classList: Array.from(element.classList),
            componentId: element.getAttribute("component-id") || "",
          },
        });
      }
    });

    setAllElements(elementsWithComponentId);
    setElements(elementsWithComponentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allActive]);

  /**
   * turn off the inspector when the inspector is not active
   */
  useEffect(() => {
    if (!isActive) {
      setElements([]);
      return;
    }
  }, [isActive]);

  function handleSelectElement(element: HTMLElement, info: ElementInfo) {
    if (element) {
      return;
    }

    onSelectElement(element);
    setElements([{ element, info }]);
    setAllActive(false);
    setIsActive(true);
  }

  if (!elements.length || (!isActive && !allActive)) {
    return <></>;
  }

  return (
    <>
      {elements.map((element, i) => (
        <ElementInspectorItem
          key={i}
          elementInfo={element.info}
          element={element.element}
          onClick={() => handleSelectElement(element.element, element.info)}
        />
      ))}
    </>
  );
}

export default ElementInspector;
